import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";

import { listMarketOfferStatusCounts, listMarketOfferStatusCountsReset } from "../../actions";
import { type OfferQuickFilterId } from "./quick-filters";
import { type OfferStatusCount } from "./types";

export type OfferStatusCounts = Partial<Record<OfferQuickFilterId, number>>;

interface RootState {
  listMarketOfferStatusCounts: {
    isFetching: boolean;
    errorMessage: string;
    data: OfferStatusCount[] | { results: [] };
  };
}

export const useOfferStatusCounts = () => {
  const dispatch = useDispatch<Dispatch<UnknownAction>>();
  const { isFetching, errorMessage, data } = useSelector(
    (state: RootState) => state.listMarketOfferStatusCounts
  );

  const refresh = useCallback(() => {
    dispatch(listMarketOfferStatusCounts() as unknown as UnknownAction);
  }, [dispatch]);

  useEffect(() => {
    refresh();

    return () => {
      dispatch(listMarketOfferStatusCountsReset() as unknown as UnknownAction);
    };
  }, [dispatch, refresh]);

  const counts = useMemo<OfferStatusCounts>(
    () =>
      Array.isArray(data)
        ? Object.fromEntries(data.map(({ status, count }) => [status, count]))
        : {},
    [data]
  );

  return { counts, refresh, isLoading: isFetching || (!Array.isArray(data) && !errorMessage) };
};
